import { envs, getEnv } from '@/config/envs';
import { axiosErrorHandler, createApiClient } from '@/lib/axios';
import { persistStorageService } from '@/features/auth/persist-storage';

// Use getEnv() to get runtime API URL (supports widget context)
const defaultApiClient = createApiClient({
  baseURL: `${getEnv().VITE_API_URL || envs.VITE_API_URL}`,
  timeout: 50000,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
  withCredentials: false,
});

// Request interceptor
defaultApiClient.interceptors.request.use(
  async (config) => {
    console.log(`🌐 Request to => ${config.url?.toUpperCase()}`);
    const token = persistStorageService.getPersistItem<string>({
      name: 'token',
    });
    // Get API key from local storage first, then fall back to environment variable
    const apiKey = persistStorageService.getPersistItem<string>({
      name: 'apiKey',
    }) || envs.VITE_X_API_KEY;

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (apiKey) {
      (config.headers as any)['x-api-key'] = apiKey;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor
defaultApiClient.interceptors.response.use(async (response) => {
  // TODO: Handle response when needed
  return response;
}, axiosErrorHandler);

// Use getEnv() to get runtime WhatsApp server URL (supports widget context)
const whatsappApiClient = createApiClient({
  baseURL: getEnv().VITE_WHATSAPP_SERVER_URL || envs.VITE_WHATSAPP_SERVER_URL,
  timeout: 15000,
  headers: {
    'X-Api-Key': getEnv().VITE_WHATSAPP_SERVER_API_KEY || envs.VITE_WHATSAPP_SERVER_API_KEY,
  },
  withCredentials: false,
});

// Request interceptor
whatsappApiClient.interceptors.request.use(
  async (config) => {
    console.log(`✉️ WhatsApp Request to => ${config.url?.toUpperCase()}`);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor
whatsappApiClient.interceptors.response.use(async (response) => {
  // TODO: Handle response when needed
  return response;
}, axiosErrorHandler);

// Messaging server client (agents-messaging)
// Derive base URL from webhook URL by removing the path
const messagingWebhookUrl = getEnv().VITE_CHANNEL_WEBHOOK_URL || envs.VITE_CHANNEL_WEBHOOK_URL || '';
const messagingBaseUrl = messagingWebhookUrl ? new URL(messagingWebhookUrl).origin : '';

const messagingApiClient = createApiClient({
  baseURL: messagingBaseUrl,
  timeout: 30000,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
  withCredentials: false,
});

// Request interceptor
messagingApiClient.interceptors.request.use(
  async (config) => {
    console.log(`📨 Messaging Request to => ${config.url?.toUpperCase()}`);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor
messagingApiClient.interceptors.response.use(async (response) => {
  return response;
}, axiosErrorHandler);

// Export all clients
export {
  defaultApiClient as default, // For backwards compatibility
  defaultApiClient,
  whatsappApiClient,
  messagingApiClient,
};

type Success<T> = {
  data: T;
  error: null;
};
type Failure<E> = {
  data: null;
  error: E;
};
type Result<T, E = Error> = Success<T> | Failure<E>;

export async function tryCatch<T, E = Error>(
  promise: Promise<T>,
): Promise<Result<T, E>> {
  try {
    const data = await promise;
    return { data, error: null };
  } catch (error) {
    return { data: null, error: error as E };
  }
}
